import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { PodcastEpisode } from '@domain/models/podcast'

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedDate = new Date(value)
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate
  }
  return undefined
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

function buildEpisodeEmbedUrl(episodeId: string): string {
  return `https://open.spotify.com/embed/episode/${episodeId}`
}

export function fromFirestorePodcastEpisode(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): PodcastEpisode {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>
  const episodeId = typeof data['episode_id'] === 'string'
    ? data['episode_id']
    : typeof data['episodeId'] === 'string'
      ? data['episodeId']
      : docSnap.id
  const releaseDateRaw = typeof data['release_date_raw'] === 'string'
    ? data['release_date_raw']
    : typeof data['releaseDateRaw'] === 'string'
      ? data['releaseDateRaw']
      : undefined

  return {
    uid: docSnap.id,
    episodeId,
    showId:
      typeof data['show_id'] === 'string'
        ? data['show_id']
        : typeof data['showId'] === 'string'
          ? data['showId']
          : '',
    name: typeof data['name'] === 'string' ? data['name'] : '',
    description:
      typeof data['description'] === 'string'
        ? data['description']
        : undefined,
    htmlDescription:
      typeof data['html_description'] === 'string'
        ? data['html_description']
        : typeof data['htmlDescription'] === 'string'
          ? data['htmlDescription']
          : undefined,
    releaseDate: toOptionalDate(data['release_date'] ?? data['releaseDate']),
    releaseDateRaw,
    releaseDatePrecision:
      typeof data['release_date_precision'] === 'string'
        ? data['release_date_precision']
        : typeof data['releaseDatePrecision'] === 'string'
          ? data['releaseDatePrecision']
          : undefined,
    durationMs:
      typeof data['duration_ms'] === 'number'
        ? data['duration_ms']
        : typeof data['durationMs'] === 'number'
          ? data['durationMs']
          : undefined,
    explicit:
      typeof data['explicit'] === 'boolean' ? data['explicit'] : undefined,
    isPlayable:
      typeof data['is_playable'] === 'boolean'
        ? data['is_playable']
        : typeof data['isPlayable'] === 'boolean'
          ? data['isPlayable']
          : undefined,
    isExternallyHosted:
      typeof data['is_externally_hosted'] === 'boolean'
        ? data['is_externally_hosted']
        : typeof data['isExternallyHosted'] === 'boolean'
          ? data['isExternallyHosted']
          : undefined,
    languages: toStringArray(data['languages']),
    imageUrl:
      typeof data['image_url'] === 'string'
        ? data['image_url']
        : typeof data['imageUrl'] === 'string'
          ? data['imageUrl']
          : undefined,
    spotifyUrl:
      typeof data['spotify_url'] === 'string'
        ? data['spotify_url']
        : typeof data['spotifyUrl'] === 'string'
          ? data['spotifyUrl']
          : undefined,
    spotifyUri:
      typeof data['spotify_uri'] === 'string'
        ? data['spotify_uri']
        : typeof data['spotifyUri'] === 'string'
          ? data['spotifyUri']
          : undefined,
    audioPreviewUrl:
      typeof data['audio_preview_url'] === 'string'
        ? data['audio_preview_url']
        : typeof data['audioPreviewUrl'] === 'string'
          ? data['audioPreviewUrl']
          : undefined,
    embedUrl:
      typeof data['embed_url'] === 'string'
        ? data['embed_url']
        : typeof data['embedUrl'] === 'string'
          ? data['embedUrl']
          : buildEpisodeEmbedUrl(episodeId),
    vimeoVideoId:
      typeof data['vimeo_video_id'] === 'string'
        ? data['vimeo_video_id']
        : typeof data['vimeoVideoId'] === 'string'
          ? data['vimeoVideoId']
          : undefined,
    vimeoPlayerUrl:
      typeof data['vimeo_player_url'] === 'string'
        ? data['vimeo_player_url']
        : typeof data['vimeoPlayerUrl'] === 'string'
          ? data['vimeoPlayerUrl']
          : undefined,
    vimeoEmbedUrl:
      typeof data['vimeo_embed_url'] === 'string'
        ? data['vimeo_embed_url']
        : typeof data['vimeoEmbedUrl'] === 'string'
          ? data['vimeoEmbedUrl']
          : undefined,
    vimeoSourcePageUrl:
      typeof data['vimeo_source_page_url'] === 'string'
        ? data['vimeo_source_page_url']
        : typeof data['vimeoSourcePageUrl'] === 'string'
          ? data['vimeoSourcePageUrl']
          : undefined,
    vimeoSourceEpisodeTitle:
      typeof data['vimeo_source_episode_title'] === 'string'
        ? data['vimeo_source_episode_title']
        : typeof data['vimeoSourceEpisodeTitle'] === 'string'
          ? data['vimeoSourceEpisodeTitle']
          : undefined,
    vimeoSourcePublishedAt: toOptionalDate(
      data['vimeo_source_published_at'] ?? data['vimeoSourcePublishedAt'],
    ),
    vimeoMatchConfidence:
      typeof data['vimeo_match_confidence'] === 'number'
        ? data['vimeo_match_confidence']
        : typeof data['vimeoMatchConfidence'] === 'number'
          ? data['vimeoMatchConfidence']
          : undefined,
    vimeoMatchReason:
      typeof data['vimeo_match_reason'] === 'string'
        ? data['vimeo_match_reason']
        : typeof data['vimeoMatchReason'] === 'string'
          ? data['vimeoMatchReason']
          : undefined,
    vimeoMatchedAt: toOptionalDate(data['vimeo_matched_at'] ?? data['vimeoMatchedAt']),
    vimeoLastSeenAt: toOptionalDate(data['vimeo_last_seen_at'] ?? data['vimeoLastSeenAt']),
    isActive:
      typeof data['is_active'] === 'boolean'
        ? data['is_active']
        : typeof data['isActive'] === 'boolean'
          ? data['isActive']
          : true,
    syncedAt: toOptionalDate(data['synced_at'] ?? data['syncedAt']),
    updatedAt: toOptionalDate(data['updated_at'] ?? data['updatedAt']),
    createdAt: toOptionalDate(data['created_at'] ?? data['createdAt']),
  }
}
